/**
 * Pure TEA update for the active worldbuilding entry frontmatter draft.
 *
 * Shell: `useActiveEntityFieldsStore.ts` (load/save Cmds + debounce timer).
 */
import { assertNever } from "../lib/assertNever";
import {
  emptyEntityFrontmatter,
  type EntityFrontmatter,
} from "../lib/entityFrontmatter";
import { isJust, isNothing, just, nothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";

export type LoadedEntityTarget = {
  readonly rootPath: string;
  readonly name: string;
};

export type ActiveEntityFieldsModel = {
  readonly fields: EntityFrontmatter;
  readonly loadedFor: Maybe<LoadedEntityTarget>;
  /** When true, the next field change should flush the debounce immediately. */
  readonly flushNext: boolean;
};

export const initialActiveEntityFieldsModel: ActiveEntityFieldsModel = {
  fields: emptyEntityFrontmatter(),
  loadedFor: nothing,
  flushNext: false,
};

export type ActiveEntityFieldsMsg =
  | { readonly type: "Cleared" }
  | {
      readonly type: "Loaded";
      readonly target: LoadedEntityTarget;
      readonly fields: EntityFrontmatter;
    }
  | {
      readonly type: "SetField";
      readonly key: keyof EntityFrontmatter;
      readonly value: EntityFrontmatter[keyof EntityFrontmatter];
      readonly immediate: boolean;
    }
  | { readonly type: "ClearFlushNext" };

export type ActiveEntityFieldsCmd =
  | { readonly kind: "scheduleSave" }
  | { readonly kind: "flushSave" }
  | { readonly kind: "refreshLexicon" };

export function updateActiveEntityFields(
  model: ActiveEntityFieldsModel,
  msg: ActiveEntityFieldsMsg,
): readonly [ActiveEntityFieldsModel, readonly ActiveEntityFieldsCmd[]] {
  switch (msg.type) {
    case "Cleared":
      return [
        {
          fields: emptyEntityFrontmatter(),
          loadedFor: nothing,
          flushNext: false,
        },
        [],
      ];
    case "Loaded":
      return [
        {
          fields: msg.fields,
          loadedFor: just(msg.target),
          flushNext: false,
        },
        [],
      ];
    case "SetField": {
      if (isNothing(model.loadedFor)) {
        return [
          {
            ...model,
            fields: { ...model.fields, [msg.key]: msg.value },
            flushNext: false,
          },
          [],
        ];
      }
      const nextFlush = msg.immediate;
      const cmds: ActiveEntityFieldsCmd[] = [
        { kind: "scheduleSave" },
        { kind: "refreshLexicon" },
      ];
      if (nextFlush) cmds.push({ kind: "flushSave" });
      return [
        {
          fields: { ...model.fields, [msg.key]: msg.value },
          loadedFor: model.loadedFor,
          flushNext: false,
        },
        cmds,
      ];
    }
    case "ClearFlushNext":
      return [{ ...model, flushNext: false }, []];
    default:
      return assertNever(msg);
  }
}

export function fieldsForLoadedEntry(
  model: ActiveEntityFieldsModel,
  rootPath: string,
  name: string,
): Maybe<EntityFrontmatter> {
  if (
    isJust(model.loadedFor) &&
    model.loadedFor.value.rootPath === rootPath &&
    model.loadedFor.value.name === name
  ) {
    return just(model.fields);
  }
  return nothing;
}

export const updateActiveEntityFieldsFn: UpdateFn<
  ActiveEntityFieldsModel,
  ActiveEntityFieldsMsg,
  ActiveEntityFieldsCmd
> = updateActiveEntityFields;
